import React, { useState } from 'react';
import { Send, AlertCircle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import SectionTitle from '../common/SectionTitle';
import Button from '../ui/Button';
import LoginModal from '../ui/LoginModal';
import '../../styles/components/Contact.css';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const formFieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.08, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const },
  }),
};

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const { isLoggedIn } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }
    setFormStatus('sending');
    setTimeout(() => {
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
    }, 1500);
  };

  return (
    <section id="contact" className="section contact">
      <div className="container contact__container">
        <SectionTitle title={t('contact.title')} subtitle={t('contact.subtitle')} />

        <motion.div
          className="contact__form-wrapper"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <motion.div
              className="contact__field"
              variants={formFieldVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
            >
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder=" "
                required
                className="contact__input"
                id="contact-name"
              />
              <label htmlFor="contact-name" className="contact__label">{t('contact.form.name')}</label>
            </motion.div>

            <motion.div
              className="contact__field"
              variants={formFieldVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
            >
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder=" "
                required
                className="contact__input"
                id="contact-email"
              />
              <label htmlFor="contact-email" className="contact__label">{t('contact.form.email')}</label>
            </motion.div>

            <motion.div
              className="contact__field"
              variants={formFieldVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={2}
            >
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder=" "
                required
                className="contact__input"
                id="contact-subject"
              />
              <label htmlFor="contact-subject" className="contact__label">{t('contact.form.subject')}</label>
            </motion.div>

            <motion.div
              className="contact__field"
              variants={formFieldVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={3}
            >
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder=" "
                required
                className="contact__input contact__textarea"
                id="contact-message"
                rows={5}
              />
              <label htmlFor="contact-message" className="contact__label">{t('contact.form.message')}</label>
            </motion.div>

            {!isLoggedIn && (
              <motion.div
                className="contact__login-notice"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <AlertCircle size={16} />
                <span>{t('contact.form.loginRequired')}</span>
              </motion.div>
            )}

            <AnimatePresence mode="wait">
              {formStatus === 'success' && (
                <motion.div
                  key="success"
                  className="contact__status contact__status--success"
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <CheckCircle size={16} />
                  <span>{t('contact.form.success')}</span>
                </motion.div>
              )}

              {formStatus === 'error' && (
                <motion.div
                  key="error"
                  className="contact__status contact__status--error"
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AlertCircle size={16} />
                  <span>{t('contact.form.error')}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              variants={formFieldVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={4}
            >
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="contact__submit"
                disabled={formStatus === 'sending'}
                icon={<Send size={16} />}
              >
                {formStatus === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
              </Button>
            </motion.div>
          </form>
        </motion.div>
      </div>

      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </section>
  );
};

export default Contact;
